import React from "react";
import type { PartSearchHit } from "../types";
import { HighlightedSearchText } from "./HighlightedSearchText";
import { TruncatedText } from "./TruncatedText";

export type PartSearchResultItemProps = {
  hit: PartSearchHit;
  showBrand?: boolean;
  showMrp?: boolean;
  onSelect: (hit: PartSearchHit) => void;
  /** When true, emphasize query matches using Typesense highlight snippets. */
  enableHighlighting?: boolean;
  query?: string;
};

export function PartSearchResultItem({
  hit,
  showBrand = true,
  showMrp = true,
  onSelect,
  enableHighlighting = false,
  query = "",
}: PartSearchResultItemProps) {
  const mrp = hit.mrp ?? 0;
  const partName = hit.partName ?? "Part";
  const partNumber = hit.partNumber;

  const partNameContent =
    enableHighlighting && query ? (
      <HighlightedSearchText
        text={partName}
        query={query}
        highlightSnippet={hit._highlightResult?.partName?.value}
      />
    ) : (
      partName
    );

  const partNumberContent =
    enableHighlighting && query && partNumber ? (
      <HighlightedSearchText
        text={partNumber}
        query={query}
        highlightSnippet={hit._highlightResult?.partNumber?.value}
      />
    ) : (
      partNumber
    );

  return (
    <button
      type="button"
      onClick={() => onSelect(hit)}
      className="w-full text-left rounded-xl border border-border/80 bg-card px-3 py-1.5 shadow-sm hover:bg-muted/40 transition-colors space-y-1.5"
    >
      <div className="flex items-center gap-2">
        {enableHighlighting && query ? (
          <span className="text-sm font-medium leading-snug min-w-0 flex-1 truncate">
            {partNameContent}
          </span>
        ) : (
          <TruncatedText
            text={partName}
            lines={1}
            className="text-sm font-medium leading-snug min-w-0 flex-1"
          />
        )}
        {showMrp ? (
          <span className="font-semibold tabular-nums text-xs text-muted-foreground shrink-0">
            ₹{mrp.toLocaleString("en-IN")}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
        {hit.partNumber ? (
          enableHighlighting && query ? (
            <span className="min-w-0 flex-1 truncate">{partNumberContent}</span>
          ) : (
            <TruncatedText text={partNumber ?? ""} lines={1} className="min-w-0 flex-1" />
          )
        ) : null}
        {showBrand && hit.brandName ? <span className="shrink-0">{hit.brandName}</span> : null}
      </div>
    </button>
  );
}
